import { Spinner } from 'components/core';
import { useOrganizations } from 'store/organizations';
import { submitServerChange } from 'components/dataCreationForms/common';
import { organizationEditOnViewToOrganizationEditModel } from 'shared/domain/organization/mapping/toModel';
import { organizationModelToOrganizationEditOnView } from 'shared/domain/organization/mapping/toView';
import { organizationClient } from 'shared/domain/organization/organizationClient';
import { OrganizationEditOnView } from 'shared/domain/organization/types/view';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { getOrganizationsStoreState } from 'store/organizations';
import { useProjectOrganizationId } from 'store/project';
import { EditOrganizationContext } from '../types';
import {
  handleOrganizationEditFormValidation,
  validateOrganizationEdit,
} from '../validation';
import { useAppNavigation } from 'contexts/navigation';

const formName = 'organization';
const SUBMIT_EVENT_TYPE = 'submit-organization';
const WithEditOrganizationContext = createContext<
  EditOrganizationContext | undefined
>(undefined);

function WithEditOrganization({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const navigate = useNavigate();
  const intl = useIntl();
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const { organizationsMap, loading: loadingOrganizations } =
    useOrganizations();
  const organizationId = useProjectOrganizationId();
  const { goToOrganizationDetails } = useAppNavigation();
  const [existingValues, setExistingValues] = useState<
    OrganizationEditOnView | undefined
  >(undefined);

  useEffect(() => {
    const existingOrganizationModel = organizationsMap.get(organizationId);
    let organizationEditOnView;
    if (existingOrganizationModel) {
      organizationEditOnView = organizationModelToOrganizationEditOnView(
        existingOrganizationModel
      );
      setExistingValues(organizationEditOnView);
    }
  }, [organizationsMap, organizationId]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const onSuccess = useCallback(
    (_id, label) => onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );

  const confirmCancel = useCancelConfirmation(formName);
  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setIsDialogOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: OrganizationEditOnView) => {
      const { saved } = await submitServerChange({
        change: () =>
          organizationClient.edit(
            organizationEditOnViewToOrganizationEditModel(values)
          ),
        setOpen: setIsDialogOpen,
        setIsPosting,
        action: 'updating an organization',
      });
      if (!saved) {
        return;
      }

      // The details view shows the organization from the store.
      await getOrganizationsStoreState().actions.reload();
      goToOrganizationDetails();
    },
    [goToOrganizationDetails]
  );

  const ctx: EditOrganizationContext = useMemo(() => {
    return {
      open: isDialogOpen,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      existingValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateOrganizationEdit,
      handleFormValidation: handleOrganizationEditFormValidation,
      onSuccess,
      formName,
    };
  }, [
    openConfirmationDialog,
    existingValues,
    isPosting,
    isDialogOpen,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    onSuccess,
  ]);
  if (loadingOrganizations) {
    return (
      <Spinner
        reason={`OrganizationWizard: loadingOrganizations. ExistingValues: ${existingValues}`}
      />
    );
  }
  return (
    <WithEditOrganizationContext.Provider value={ctx}>
      {children}
    </WithEditOrganizationContext.Provider>
  );
}

function useEditOrganization(): EditOrganizationContext {
  const context = useContext(WithEditOrganizationContext);
  if (context === undefined) {
    throw new Error(
      'useEditOrganization must be used within an EditOrganizationContext'
    );
  }
  return context;
}

const withEditOrganization =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithEditOrganization>
      <Component {...props} />
    </WithEditOrganization>
  );

export { WithEditOrganization, useEditOrganization, withEditOrganization };
